import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, getAuthUser, logout } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { LogOut, Fuel, ArrowRight, CheckCircle2, XCircle, RotateCcw, AlertTriangle } from "lucide-react";

export default function WorkerDashboard() {
  const navigate = useNavigate();
  const user = getAuthUser();
  const [code, setCode] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const lookup = async (e) => {
    e?.preventDefault();
    const c = code.trim().toUpperCase();
    if (!/^[A-Z]{6}$/.test(c)) {
      toast.error("Ingresa un código de 6 letras");
      return;
    }
    setLoading(true);
    setNotFound(false);
    setOrder(null);
    try {
      const { data } = await api.get(`/worker/orders/${c}`);
      setOrder(data);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const dispatch = async () => {
    if (!order) return;
    setDispatching(true);
    try {
      const { data } = await api.post(`/worker/orders/${order.code}/dispatch`);
      setOrder(data);
      toast.success("Orden despachada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setDispatching(false);
    }
  };

  const reset = () => {
    setOrder(null);
    setCode("");
    setNotFound(false);
    setConfirmed(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <BrandHeader
        right={
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-400/10 border border-blue-400/20">
              <Fuel className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-xs font-mono-display uppercase tracking-widest text-blue-300">
                Estación · {user?.name}
              </span>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Salir
            </Button>
          </div>
        }
      />

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-16">
        {!order && !notFound && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-mono-display mb-4">
              Verificación de orden
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl mb-2">Introduce el código</h1>
            <p className="text-zinc-400 mb-10">6 letras entregadas al cliente tras pago Zelle.</p>

            <form onSubmit={lookup} className="max-w-md mx-auto" data-testid="lookup-form">
              <Input
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 6))}
                placeholder="ABCDEF"
                data-testid="code-input"
                className="text-center font-mono-display text-4xl md:text-6xl tracking-[0.4em] h-24 md:h-32 bg-zinc-900 border-white/10 text-yellow-400 focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
                maxLength={6}
              />
              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                data-testid="lookup-btn"
                className="mt-6 w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-12 text-base"
              >
                {loading ? "Buscando..." : "Buscar orden"}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          </motion.section>
        )}

        {notFound && (
          <motion.section
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h2 className="font-display font-bold text-3xl mb-2">Código no encontrado</h2>
            <p className="text-zinc-400 mb-8">Verifica que el código sea correcto e intenta de nuevo.</p>
            <Button onClick={reset} data-testid="try-again-btn" className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Intentar de nuevo
            </Button>
          </motion.section>
        )}

        {order && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-mono-display mb-2">
                Orden encontrada
              </div>
              <div className="font-mono-display font-black text-5xl md:text-7xl tracking-[0.2em] text-yellow-400 code-glow" data-testid="found-code">
                {order.code}
              </div>
            </div>

            <div className="glass rounded-xl p-8 md:p-10">
              {/* Prominent fuel badge — the worker MUST know exactly which fuel to dispatch */}
              <FuelBanner label={order.fuel_label} octane={order.fuel_octane} />

              <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                <div>
                  <Row label="Cliente" value={order.name} testid="row-name" />
                  <Row label="Email" value={order.email} testid="row-email" />
                  <Row label="Teléfono" value={order.phone} testid="row-phone" />
                </div>
                <div>
                  <Row
                    label="Combustible"
                    value={order.fuel_label ? `${order.fuel_label}${order.fuel_octane ? " · " + order.fuel_octane : ""}` : "Gasolina B94"}
                    testid="row-fuel"
                  />
                  <Row label="Litros a despachar" value={(order.liters ?? order.gallons ?? 0).toString()} big highlight testid="row-liters" />
                  <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-mono-display mb-2">Estado</div>
                    <StatusPill status={order.status} />
                  </div>
                </div>
              </div>

              {order.status === "pending" && (
                <div
                  className="mt-8 flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3"
                  data-testid="pending-payment-warning"
                >
                  <AlertTriangle className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-amber-200">
                      Pago pendiente — NO despachar
                    </div>
                    <div className="text-xs text-amber-100/70 mt-0.5">
                      Este código está reservado, pero el pago del cliente aún no ha sido confirmado. Espera a que el sistema valide el pago Zelle o Stripe antes de despachar.
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                {order.status === "confirmed" && (
                  <>
                    <label
                      htmlFor="confirm-dispatch"
                      className="flex-1 min-w-[280px] flex items-start gap-3 border border-white/10 bg-zinc-900 hover:border-emerald-500/40 rounded-md px-4 py-3 cursor-pointer transition-colors"
                      data-testid="confirm-dispatch-box"
                    >
                      <Checkbox
                        id="confirm-dispatch"
                        checked={confirmed}
                        onCheckedChange={(v) => setConfirmed(!!v)}
                        data-testid="confirm-dispatch-checkbox"
                        className="mt-0.5 border-white/30 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                      />
                      <div>
                        <div className="text-sm font-semibold text-white">
                          Confirmo entrega de {order.liters ?? order.gallons} litros de{" "}
                          <span className="text-yellow-300">
                            {order.fuel_label || "Gasolina B94"}
                            {order.fuel_octane ? ` (${order.fuel_octane})` : ""}
                          </span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          Cliente: <span className="text-zinc-300">{order.name}</span> · Marca esta casilla antes de despachar
                        </div>
                      </div>
                    </label>
                    <Button
                      onClick={dispatch}
                      disabled={dispatching || !confirmed}
                      data-testid="dispatch-btn"
                      className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-800 disabled:text-zinc-500 text-white font-bold h-12"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1.5" />
                      {dispatching ? "Despachando..." : "Marcar como despachado"}
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={reset}
                  data-testid="new-lookup-btn"
                  className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 h-12"
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Nueva búsqueda
                </Button>
              </div>
            </div>
          </motion.section>
        )}
      </main>
    </div>
  );
}

function Row({ label, value, big = false, highlight = false, testid }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-mono-display mb-1">
        {label}
      </div>
      <div
        className={`font-medium ${big ? "font-display font-black text-4xl md:text-5xl" : "text-lg"} ${highlight ? "text-yellow-400" : "text-white"}`}
        data-testid={testid}
      >
        {value}
      </div>
    </div>
  );
}

function FuelBanner({ label, octane }) {
  const name = label || "Gasolina B94";
  return (
    <div
      data-testid="worker-fuel-banner"
      className="mb-6 flex items-center gap-4 rounded-xl border border-yellow-400/40 bg-yellow-400/10 px-5 py-4"
    >
      <div className="shrink-0 w-11 h-11 rounded-full bg-yellow-400/20 border border-yellow-400/40 flex items-center justify-center">
        <Fuel className="w-5 h-5 text-yellow-300" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.32em] text-yellow-300 font-mono-display">
          Combustible a despachar
        </div>
        <div
          className="font-display font-black text-2xl md:text-3xl text-yellow-100 mt-0.5 truncate"
          data-testid="worker-fuel-label"
        >
          {name}
          {octane && (
            <span
              className="ml-2 text-sm md:text-base font-mono-display font-bold text-yellow-300/90 uppercase tracking-widest align-middle"
              data-testid="worker-fuel-octane"
            >
              {octane}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    confirmed: { text: "Confirmada · Lista para despacho", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
    dispatched: { text: "Ya despachada", cls: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
    pending: { text: "Pendiente de pago", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
    cancelled: { text: "Cancelada", cls: "bg-zinc-700 text-zinc-400 border-zinc-600" },
  };
  const s = map[status] || { text: status, cls: "bg-zinc-800 text-zinc-300" };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border ${s.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      <span className="text-sm font-medium">{s.text}</span>
    </div>
  );
}
